import React, {useEffect, useState} from 'react'
import {KTIcon} from '../../../_metronic/helpers'
import {restRequest} from '../../../api-utils/apiQueries'

type MetricItem = {
  label: string
  value: string | number
  icon: string
  color: 'primary' | 'success' | 'warning' | 'danger' | 'info'
}

const money = (value: any) => `$${Number(value ?? 0).toFixed(2)}`

const MetricRow = ({label, value, icon, color}: MetricItem) => (
  <div className='d-flex align-items-center justify-content-between gap-4 rounded border border-gray-200 bg-white px-5 py-4'>
    <div className='d-flex align-items-center gap-4 min-w-0'>
      <span className={`symbol symbol-44px bg-light-${color} flex-shrink-0`}>
        <span className='symbol-label bg-transparent'>
          <KTIcon iconName={icon} className={`fs-2 text-${color}`} />
        </span>
      </span>
      <div className='min-w-0'>
        <div className='text-gray-700 fw-semibold fs-6'>{label}</div>
      </div>
    </div>
    <div className='fs-2 fw-bold text-gray-900 text-nowrap'>{value}</div>
  </div>
)

const MetricTile = ({label, value, icon, color}: MetricItem) => (
  <div className={`bg-light-${color} rounded border border-${color} border-opacity-10 p-5 h-100`}>
    <div className='d-flex align-items-center justify-content-between gap-4'>
      <div className='min-w-0'>
        <div className={`text-${color} fw-semibold fs-7 text-uppercase mb-2`}>{label}</div>
        <div className='fs-2 fw-bold text-gray-900'>{value}</div>
      </div>
      <span className={`symbol symbol-44px bg-white flex-shrink-0`}>
        <span className='symbol-label bg-white'>
          <KTIcon iconName={icon} className={`fs-2 text-${color}`} />
        </span>
      </span>
    </div>
  </div>
)

const OverviewCard = ({
  title,
  subtitle,
  items,
  variant = 'list',
}: {
  title: string
  subtitle: string
  items: MetricItem[]
  variant?: 'list' | 'grid'
}) => (
  <div className='card h-100 shadow-sm border-0'>
    <div className='card-header border-0 pt-6 pb-0'>
      <div>
        <h3 className='card-title fw-bold text-gray-900 mb-1'>{title}</h3>
        <div className='text-muted fs-7'>{subtitle}</div>
      </div>
    </div>
    <div className='card-body'>
      {variant === 'grid' ? (
        <div className='row g-4'>
          {items.map((item) => (
            <div className='col-md-6 col-xl-4' key={item.label}>
              <MetricTile {...item} />
            </div>
          ))}
        </div>
      ) : (
        <div className='d-flex flex-column gap-4'>
          {items.map((item) => (
            <MetricRow key={item.label} {...item} />
          ))}
        </div>
      )}
    </div>
  </div>
)

const UserOverview = ({filter}: any) => {
  const [users, setUsers] = useState<any>(null)
  const [trips, setTrips] = useState<any>(null)
  const [gift, setGift] = useState<any>(null)
  const [wallet, setWallet] = useState<any>(null)
  const [isLoader, setIsLoader] = useState(true)

  const fetchAllUsersData = async () => {
    try {
      setIsLoader(true)
      const response = await restRequest.getDashboardAllData(filter)
      const data = response?.data.data
      setUsers(data.user)
      setTrips(data.trip)
      setGift(data.gift_card)
      setWallet(data.wallet)
    } catch {
      /* 401: global axios handler redirects */
    } finally {
      setIsLoader(false)
    }
  }
  useEffect(() => {
    fetchAllUsersData()
  }, [filter])

  const userItems: MetricItem[] = [
    {
      label: 'Total users',
      value: users ? users.total_users : 0,
      icon: 'profile-user',
      color: 'warning',
    },
    {
      label: 'Active Free Trial',
      value: users ? users.active_free_trial ?? 0 : 0,
      icon: 'timer',
      color: 'info',
    },
    {
      label: 'Subscribed',
      value: users ? users.subscribed ?? 0 : 0,
      icon: 'verify',
      color: 'success',
    },
    {
      label: 'Inactive Subscription',
      value: users ? users.inactive_subscription ?? 0 : 0,
      icon: 'cross-circle',
      color: 'danger',
    },
    {
      label: 'Active Passengers',
      value: users ? users.active_passengers : 0,
      icon: 'people',
      color: 'primary',
    },
  ]

  const tripItems: MetricItem[] = [
    {
      label: 'Total Trips Scheduled',
      value: trips ? trips.total_scheduled_trips : 0,
      icon: 'calendar',
      color: 'primary',
    },
    {
      label: 'Total Trips Completed',
      value: trips ? trips.total_completed_trips : 0,
      icon: 'check-circle',
      color: 'success',
    },
    {
      label: 'Total Trips Cancelled',
      value: trips ? trips.total_cancelled_trips : 0,
      icon: 'cross-circle',
      color: 'danger',
    },
    {
      label: 'Total Trips Unfulfilled',
      value: trips ? trips.total_unsuccessful_trips : 0,
      icon: 'information-5',
      color: 'warning',
    },
  ]

  const giftItems: MetricItem[] = [
    {
      label: 'Total Cards Claimed',
      value: gift ? gift.total_gift_card_claimed : 0,
      icon: 'gift',
      color: 'danger',
    },
    {
      label: 'Total Value of Cards Claimed',
      value: `$${gift ? gift.total_value_of_points_claimed : 0}`,
      icon: 'dollar',
      color: 'danger',
    },
    {
      label: 'Total points unclaimed by driver',
      value: `$${gift ? gift.total_unclaimed_points : 0}`,
      icon: 'wallet',
      color: 'warning',
    },
  ]

  const walletItems: MetricItem[] = [
    {
      label: 'Total Transactions (Driver and Passenger)',
      value: wallet ? wallet.total_transaction : 0,
      icon: 'wallet',
      color: 'info',
    },
    {
      label: 'Total Transactions Value (Driver and Passenger)',
      value: money(wallet?.total_transaction_value),
      icon: 'dollar',
      color: 'info',
    },
    {
      label: 'Total Driver Subscriptions',
      value: wallet ? wallet.total_subscription_purchase : 0,
      icon: 'badge',
      color: 'primary',
    },
    {
      label: 'Total Driver Subscriptions Value',
      value: money(wallet?.total_subscription_purchase_value),
      icon: 'dollar',
      color: 'primary',
    },
    {
      label: 'Total Transactions Credit Purchase (Points)',
      value: wallet ? wallet.total_credit_purchase_in_points : 0,
      icon: 'two-credit-cart',
      color: 'success',
    },
    {
      label: 'Total Transactions value Credit Purchase',
      value: money(wallet?.total_credit_purchase_value),
      icon: 'dollar',
      color: 'success',
    },
  ]

  return (
    <>
      {isLoader ? (
        <div className='card card-flush h-250px d-flex align-items-center justify-content-center'>
          <div className='spinner-border text-primary'></div>
        </div>
      ) : (
        <div className='d-flex flex-column gap-6'>
          <div className='row g-6'>
            <div className='col-xl-4'>
              <OverviewCard
                title='Users overview'
                subtitle='Subscription and passenger activity'
                items={userItems}
              />
            </div>
            <div className='col-xl-4'>
              <OverviewCard
                title='Trips overview'
                subtitle='Scheduled, completed and failed trip outcomes'
                items={tripItems}
              />
            </div>
            <div className='col-xl-4'>
              <OverviewCard
                title='Gift Card Overview'
                subtitle='Claims and unclaimed point value'
                items={giftItems}
              />
            </div>
          </div>

          <OverviewCard
            title='Wallet overview'
            subtitle='Driver subscriptions, passenger purchases and transaction value'
            items={walletItems}
            variant='grid'
          />
        </div>
      )}
    </>
  )
}

export default UserOverview
